import { Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { IMPACT_FONTS } from '../fonts/fonts';

export interface CounterProps {
  initialValue: number;
  targetNumber: number;
  animationTime: number;
  fontSize: string;
  fontOutlineSize: string;
  outlineColour?(currentCount: number): string;
  minBoundaryNumber: number;
  maxBoundaryNumber: number;
  sizingMagnitude?: number;
  minDigits?: number;
}

export const CounterComponent = (props: CounterProps) => {
  // Destructure minDigits from props
  const { initialValue, targetNumber, animationTime, minDigits } = props;

  const getOutlineColour = useCallback(
    (currentCount:number) => {
      if (props.outlineColour) {
        return props.outlineColour(currentCount);
      }
      return 'white';
    },
    [props]
  );

  const getFontSize = useCallback(
    (currentCount:number) => {
      if (props.sizingMagnitude) {
        return `calc(${props.fontSize} * ${Math.pow(Math.max(props.minBoundaryNumber, Math.min(props.maxBoundaryNumber, currentCount)), props.sizingMagnitude)})`;
      }
      return props.fontSize;
    },
    [props]
  );

  const [countDetails, setCountDetails] = useState({
    initialValue: initialValue,
    currentCount: initialValue,
    animationTime: animationTime,
    defaultTimeout: animationTime / Math.abs(targetNumber - initialValue),
    startTime: Date.now(),
    outlineColour: getOutlineColour(initialValue),
    fontSize: getFontSize(initialValue),
    incrementing: targetNumber >= initialValue,
    targetNumber: targetNumber,
  });

  //Update target number on the fly
  useEffect(() => {
    if (countDetails.targetNumber !== targetNumber) {
      setCountDetails((currentCountDetails) => {
        return {
          ...currentCountDetails,
          animationTime: animationTime,
          initialValue: currentCountDetails.currentCount,
          startTime: Date.now(),
          defaultTimeout:
            animationTime /
            Math.abs(targetNumber - currentCountDetails.currentCount),
          incrementing: targetNumber >= currentCountDetails.currentCount,
          targetNumber: targetNumber,
        };
      });
    }
  }, [targetNumber, animationTime, countDetails.targetNumber]);

  useEffect(() => {
    const scoreDifference =
      countDetails.targetNumber - countDetails.initialValue;
    if (scoreDifference !== 0) {
      setTimeout(() => {
        setCountDetails((currentCountDetails) => {
          const currentTime = Date.now();
          const timeElapsed = currentTime - currentCountDetails.startTime;
          //This logic is wrong, need to reconsider
          const portionRemaining = Math.min(
            1,
            timeElapsed / currentCountDetails.animationTime
          );
          const initialGap =
            currentCountDetails.targetNumber - currentCountDetails.initialValue;
          // console.log(`
          //     timeElapsed: ${timeElapsed}
          //     timeRemaining: ${portionRemaining}
          //     currentCount: ${currentCountDetails.currentCount}
          //     initialGap: ${initialGap}
          //     targetNumber: ${currentCountDetails.targetNumber}
          //      Math.floor((initialGap) * (portionRemaining)): ${Math.floor((initialGap) * (portionRemaining))}
          //     newScore:  ${currentCountDetails.initialValue + Math.floor((initialGap) * (portionRemaining))}
          //      `)
          const newScore =
            currentCountDetails.initialValue +
            Math.floor(initialGap * portionRemaining);
          return {
            ...currentCountDetails,
            currentCount: newScore,
            outlineColour: getOutlineColour(newScore),
            fontSize: getFontSize(newScore),
          };
        });
      }, countDetails.defaultTimeout);
    }
  }, [countDetails, getFontSize, getOutlineColour]);

  // Format the number with leading zeros if minDigits is set
  const formattedCount = minDigits
    ? String(countDetails.currentCount).padStart(minDigits, '0')
    : countDetails.currentCount;

  return (
    <Typography
      sx={{
        color: 'black',
        fontSize: countDetails.fontSize,
        fontFamily: IMPACT_FONTS,
        textShadow: `
                    -${props.fontOutlineSize} -${props.fontOutlineSize} 0 ${countDetails.outlineColour},  
                     ${props.fontOutlineSize} -${props.fontOutlineSize} 0 ${countDetails.outlineColour},
                    -${props.fontOutlineSize}  ${props.fontOutlineSize} 0 ${countDetails.outlineColour},
                     ${props.fontOutlineSize}  ${props.fontOutlineSize} 0 ${countDetails.outlineColour}`,
      }}
    >
      {/* Render the formatted count */}
      {formattedCount}
    </Typography>
  );
};
